import React from "react";
import {
  Zap,
  ListChecks,
  BarChart3,
  Briefcase,
  Timer,
  AlertTriangle,
  Target,
  Plug,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const FEATURES = [
  {
    icon: Zap,
    title: "Real-time validation",
    description:
      "Verify emails instantly as users type. Perfect for forms, signups, and lead capture with sub-300ms response times.",
    color: "amber",
  },
  {
    icon: ListChecks,
    title: "Bulk list cleaning",
    description:
      "Upload and validate thousands of emails in one go. Clean your entire database before campaigns launch.",
    color: "sky",
  },
  {
    icon: BarChart3,
    title: "Deliverability & scoring",
    description:
      "Get health scores for every email. Make smart decisions about which contacts to send to and which to suppress.",
    color: "emerald",
  },
  {
    icon: Briefcase,
    title: "Role-based email detection",
    description:
      "Identify generic addresses like info@, support@, and admin@ that hurt engagement rates and deliverability.",
    color: "blue",
  },
  {
    icon: Timer,
    title: "Disposable / temporary detection",
    description:
      "Catch temporary email services that expire quickly. Protect your list from fake signups and low-quality leads.",
    color: "violet",
  },
  {
    icon: AlertTriangle,
    title: "Abuse & spam trap detection",
    description:
      "Flag known spam traps and abuse addresses before they damage your sender reputation and trigger blacklists.",
    color: "red",
  },
  {
    icon: Target,
    title: "Catch-all handling with confidence scoring",
    description:
      "Smart detection for catch-all domains with risk scoring. Decide whether to send based on your risk tolerance.",
    color: "indigo",
  },
  {
    icon: Plug,
    title: "API & integrations library",
    description:
      "RESTful API, webhooks, and pre-built integrations for ESPs, CRMs, forms, and automation tools. Easy setup.",
    color: "teal",
  },
];

const colorClasses = {
  amber: {
    bg: "bg-amber-50 dark:bg-amber-900/10",
    iconBg: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
    gradient: "from-amber-500 to-orange-600",
  },
  sky: {
    bg: "bg-sky-50 dark:bg-sky-900/10",
    iconBg: "bg-sky-500",
    text: "text-sky-600 dark:text-sky-400",
    gradient: "from-sky-500 to-blue-600",
  },
  emerald: {
    bg: "bg-emerald-50 dark:bg-emerald-900/10",
    iconBg: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
    gradient: "from-emerald-500 to-teal-600",
  },
  blue: {
    bg: "bg-blue-50 dark:bg-blue-900/10",
    iconBg: "bg-blue-500",
    text: "text-blue-600 dark:text-blue-400",
    gradient: "from-blue-500 to-indigo-600",
  },
  violet: {
    bg: "bg-violet-50 dark:bg-violet-900/10",
    iconBg: "bg-violet-500",
    text: "text-violet-600 dark:text-violet-400",
    gradient: "from-violet-500 to-purple-600",
  },
  red: {
    bg: "bg-red-50 dark:bg-red-900/10",
    iconBg: "bg-red-500",
    text: "text-red-600 dark:text-red-400",
    gradient: "from-red-500 to-rose-600",
  },
  indigo: {
    bg: "bg-indigo-50 dark:bg-indigo-900/10",
    iconBg: "bg-indigo-500",
    text: "text-indigo-600 dark:text-indigo-400",
    gradient: "from-indigo-500 to-purple-600",
  },
  teal: {
    bg: "bg-teal-50 dark:bg-teal-900/10",
    iconBg: "bg-teal-500",
    text: "text-teal-600 dark:text-teal-400",
    gradient: "from-teal-500 to-cyan-600",
  },
};
export default function FeatureGridSection() {
    const navigate = useNavigate()
    const handleExplore = () => {
        navigate("/product/email-validation");
    }
  return (
    <section className="py-16 lg:py-24 bg-white dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            Everything you need to{" "}
            <span className="text-sky-600 dark:text-sky-400">
              trust your email list
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            Comprehensive email validation tools that catch every type of
            invalid, risky, or problematic address
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {FEATURES.map((feature, index) => {
            const Icon = feature.icon;
            const colors = colorClasses[feature.color];

            return (
              <div
                key={index}
                className="group relative bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div
                  className={`absolute inset-0 ${colors.bg} opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300`}
                />

                <div className="relative">
                  <div
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${colors.gradient} mb-4 shadow-md group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                    {feature.title}
                  </h3>

                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                <div
                  className={`absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl ${colors.gradient} opacity-0 group-hover:opacity-5 rounded-tl-full transition-opacity duration-300`}
                />
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            All features work together to give you the cleanest, most
            deliverable email list possible
          </p>
          <button
          onClick={handleExplore}
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold border-2 border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white dark:border-sky-500 dark:text-sky-400 dark:hover:bg-sky-500 dark:hover:text-white transition-all duration-300">
            Explore all features
          </button>
        </div>
      </div>
    </section>
  );
}
